import React, { useMemo } from 'react';

/**
 * 배치 해석 결과 요약 — 시추공 카드 그리드.
 *
 * 2025년까지 결과 화면은 CSV 를 그대로 옮긴 표 하나였다. 값은 있었지만
 * 어느 시추공이 위험한지, 어느 재현주기에서 위험해지는지 한눈에 들어오지 않았다.
 * 여기서는 시추공별로 재현주기 3종(500/1,000/2,400년)의 LPI 를 한 장의 카드에 모아
 * 등급 색으로 표시하고, 카드를 누르면 심도별 상세 해석으로 들어가게 한다.
 */

// Iwasaki(1978) 액상화 가능성 지수 등급 경계
export const lpiLevel = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return { key: '-', label: '-', tone: 'gray' };
  if (n <= 0) return { key: 'vlow', label: '매우낮음', tone: 'green' };
  if (n <= 5) return { key: 'low', label: '낮음', tone: 'yellow' };
  if (n <= 15) return { key: 'high', label: '높음', tone: 'orange' };
  return { key: 'vhigh', label: '매우높음', tone: 'red' };
};

const TONE = {
  green: { chip: 'bg-green-100 text-green-700', bar: 'bg-green-500', text: 'text-green-600' },
  yellow: { chip: 'bg-yellow-100 text-yellow-700', bar: 'bg-yellow-500', text: 'text-yellow-600' },
  orange: { chip: 'bg-orange-100 text-orange-700', bar: 'bg-orange-500', text: 'text-orange-600' },
  red: { chip: 'bg-red-100 text-red-700', bar: 'bg-red-500', text: 'text-red-600' },
  gray: { chip: 'bg-gray-100 text-gray-500', bar: 'bg-gray-300', text: 'text-gray-400' },
};

// 재현주기 표기 순서 (CSV 의 group 값)
const PERIODS = ['500', '1000', '2400'];

const LPISummaryCards = ({ lpiResult, onSelect }) => {
  // 시추공별로 재현주기별 LPI 를 모은다.
  const byBorehole = useMemo(() => {
    const map = new Map();
    (lpiResult || []).forEach((row) => {
      const name = row.Borehole_name;
      if (!map.has(name)) map.set(name, { name, periods: {}, max: 0 });
      const entry = map.get(name);
      const value = Number(row.LPI);
      entry.periods[String(row.group)] = value;
      if (Number.isFinite(value)) entry.max = Math.max(entry.max, value);
    });
    // 위험한 시추공이 먼저 보이도록 정렬한다.
    return [...map.values()].sort((a, b) => b.max - a.max);
  }, [lpiResult]);

  if (!byBorehole.length) return null;

  const worst = byBorehole[0].max;

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {byBorehole.map((b) => {
          const lv = lpiLevel(b.max);
          const tone = TONE[lv.tone];
          return (
            <button
              key={b.name}
              onClick={() => onSelect?.(b.name)}
              className="text-left bg-white border border-gray-200 rounded-lg p-4
                         hover:border-primary-400 hover:shadow-medium transition group"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm font-semibold text-gray-800">{b.name}</div>
                  <div className="text-[11px] text-gray-400 mt-0.5">최대 LPI</div>
                </div>
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${tone.chip}`}>
                  {lv.label}
                </span>
              </div>

              <div className={`mt-1 text-3xl font-extrabold tabular-nums ${tone.text}`}>
                {Number.isFinite(b.max) ? b.max.toFixed(1) : '-'}
              </div>

              <div className="mt-3 space-y-1.5">
                {PERIODS.map((p) => {
                  const v = b.periods[p];
                  const has = Number.isFinite(v);
                  const ratio = has && worst > 0 ? Math.min(1, v / worst) : 0;
                  const t = TONE[lpiLevel(v).tone];
                  return (
                    <div key={p} className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-400 w-14 shrink-0">
                        {Number(p).toLocaleString()}년
                      </span>
                      <span className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <span
                          className={`block h-full rounded-full ${t.bar}`}
                          style={{ width: `${Math.max(ratio * 100, has && v > 0 ? 4 : 0)}%` }}
                        />
                      </span>
                      <span className="text-[11px] tabular-nums text-gray-600 w-8 text-right">
                        {has ? v.toFixed(1) : '-'}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="mt-3 text-[11px] text-primary-600 opacity-0 group-hover:opacity-100 transition">
                심도별 상세 해석 보기 →
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default LPISummaryCards;
